/**
 * tokenStorage.js — multi-tier session persistence
 * ─────────────────────────────────────────────────────────────────────
 * Tim's P0 brief Feb 17: "Brugeren skal kunne lukke appen, genåbne
 * dagen efter, stadig være logget ind." iOS Safari ITP purger
 * localStorage efter ~7 dage uden interaktion. Cookies har en SEPARAT
 * (mere generøs) retention-politik. Ved at skrive token til BÅDE
 * localStorage OG en non-httpOnly cookie OG sessionStorage får vi 3
 * redundante storage-lag — chancen for at minimum ÉT overlever ITP
 * mellem brugerbesøg er meget højere end ved en enkelt kilde.
 *
 * Læsestrategi (prioritet):
 *   1. localStorage (hurtig, primær)
 *   2. cookie (overlever ITP når localStorage purges)
 *   3. sessionStorage (overlever fanen, ikke browser-genstart)
 *
 * Skrivestrategi:
 *   Skriv til ALLE tre kilder ved login + ved hver token-rotation.
 *
 * Cookie-attributter:
 *   - Path=/  (tilgængelig på alle ruter)
 *   - Max-Age=31536000  (365 dage)
 *   - SameSite=Lax  (same-origin requests OK, CSRF-beskyttet)
 *   - Secure  (HTTPS only — sat dynamisk når protokol = https)
 *   - IKKE HttpOnly (vi har brug for JS-adgang for at populate axios)
 *
 * Sikkerheds-trade-off: en non-HttpOnly cookie er sårbar over for XSS.
 * Men: vi har ALLEREDE token i localStorage som JS kan læse — så
 * angrebsoverfladen er identisk. Cookien tilføjer ITP-overlevelse
 * uden at gøre ting værre.
 */

const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const COOKIE_NAME = 'bm_session';
const COOKIE_MAX_AGE_S = 365 * 24 * 60 * 60; // 365 dage

const isSecure = () =>
  typeof window !== 'undefined' && window.location?.protocol === 'https:';

// ── Cookie helpers ──────────────────────────────────────────────────
const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const target = `${name}=`;
  const parts = (document.cookie || '').split(';');
  for (let i = 0; i < parts.length; i++) {
    const c = parts[i].trim();
    if (c.indexOf(target) === 0) {
      try { return decodeURIComponent(c.substring(target.length)); }
      catch { return c.substring(target.length); }
    }
  }
  return null;
};

const writeCookie = (name, value, maxAgeSec = COOKIE_MAX_AGE_S) => {
  if (typeof document === 'undefined') return;
  const enc = encodeURIComponent(value);
  const secure = isSecure() ? '; Secure' : '';
  document.cookie = `${name}=${enc}; Path=/; Max-Age=${maxAgeSec}; SameSite=Lax${secure}`;
};

const clearCookie = (name) => {
  if (typeof document === 'undefined') return;
  const secure = isSecure() ? '; Secure' : '';
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
};

// ── Safe wrappers (fail closed if storage throws — Safari private mode) ─
const safeGet = (storage, key) => {
  try { return storage?.getItem(key) || null; } catch { return null; }
};
const safeSet = (storage, key, val) => {
  try { storage?.setItem(key, val); } catch { /* ignore quota/security errors */ }
};
const safeRemove = (storage, key) => {
  try { storage?.removeItem(key); } catch { /* ignore */ }
};

// ── Public API ──────────────────────────────────────────────────────
export const readToken = () => {
  // Prioritetsrækkefølge: localStorage → cookie → sessionStorage.
  // Hvis token findes i cookie men ikke i localStorage, betyder det
  // mest sandsynligt at ITP purgede localStorage — vi heal'er ved at
  // skrive token TILBAGE til localStorage så efterfølgende reads er
  // hurtige.
  const ls = safeGet(typeof window !== 'undefined' ? window.localStorage : null, TOKEN_KEY);
  if (ls) return ls;
  const ck = readCookie(COOKIE_NAME);
  if (ck) {
    // Heal: kopier tilbage til localStorage.
    safeSet(typeof window !== 'undefined' ? window.localStorage : null, TOKEN_KEY, ck);
    return ck;
  }
  const ss = safeGet(typeof window !== 'undefined' ? window.sessionStorage : null, TOKEN_KEY);
  if (ss) {
    safeSet(typeof window !== 'undefined' ? window.localStorage : null, TOKEN_KEY, ss);
    return ss;
  }
  return null;
};

export const writeToken = (token) => {
  if (!token) return;
  const ls = typeof window !== 'undefined' ? window.localStorage : null;
  const ss = typeof window !== 'undefined' ? window.sessionStorage : null;
  safeSet(ls, TOKEN_KEY, token);
  safeSet(ss, TOKEN_KEY, token);
  writeCookie(COOKIE_NAME, token);
};

export const clearToken = () => {
  const ls = typeof window !== 'undefined' ? window.localStorage : null;
  const ss = typeof window !== 'undefined' ? window.sessionStorage : null;
  safeRemove(ls, TOKEN_KEY);
  safeRemove(ss, TOKEN_KEY);
  clearCookie(COOKIE_NAME);
};

export const readUser = () => {
  const ls = typeof window !== 'undefined' ? window.localStorage : null;
  const raw = safeGet(ls, USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

export const writeUser = (user) => {
  if (!user) return;
  const ls = typeof window !== 'undefined' ? window.localStorage : null;
  safeSet(ls, USER_KEY, JSON.stringify(user));
};

export const clearUser = () => {
  const ls = typeof window !== 'undefined' ? window.localStorage : null;
  safeRemove(ls, USER_KEY);
};
